import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, AlertTriangle, CheckCircle, XCircle, Loader2, Upload, FileCode } from 'lucide-react';

const SecurityAnalyzer = () => {
  const [code, setCode] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const exampleCode = `// Example DeFi Strategy Contract
pragma solidity ^0.8.0;

contract ExampleStrategy {
    mapping(address => uint256) public balances;
    
    function deposit() public payable {
        balances[msg.sender] += msg.value;
    }
    
    function withdraw(uint256 amount) public {
        require(balances[msg.sender] >= amount, "Insufficient balance");
        balances[msg.sender] -= amount;
        payable(msg.sender).transfer(amount);
    }
}`;

  const handleAnalyze = async () => {
    if (!code.trim()) return;

    setIsAnalyzing(true);
    setAnalysis(null);

    // Simulate AI security analysis (in production, this should call a real AI API)
    setTimeout(() => {
      const mockAnalysis = {
        overallScore: 72,
        riskLevel: 'medium',
        vulnerabilities: [
          {
            type: 'Reentrancy',
            severity: 'high',
            description: 'The withdraw function has reentrancy attack risk, recommend using ReentrancyGuard',
            line: 12,
            recommendation: 'Add @openzeppelin/contracts/security/ReentrancyGuard.sol protection'
          },
          {
            type: 'Integer Overflow',
            severity: 'medium',
            description: 'Balance calculation may have overflow risk, recommend using SafeMath',
            line: 8,
            recommendation: 'Use SafeMath library for mathematical operations'
          },
          {
            type: 'Access Control',
            severity: 'low',
            description: 'Missing access control mechanism, recommend adding Ownable or role management',
            line: 1,
            recommendation: 'Implement appropriate access control patterns'
          }
        ],
        recommendations: [
          'Use OpenZeppelin security libraries (ReentrancyGuard, SafeMath)',
          'Implement complete access control mechanisms',
          'Add event logs for tracking',
          'Perform comprehensive unit tests and integration tests',
          'Consider using formal verification tools'
        ],
        gasOptimization: [
          'Using uint256 instead of uint can save gas',
          'Consider using packed storage to optimize storage layout'
        ]
      };
      setAnalysis(mockAnalysis);
      setIsAnalyzing(false);
    }, 2500);
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'text-red-400 bg-red-500/10 border-red-500/30';
      case 'medium':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
      case 'low':
        return 'text-blue-400 bg-blue-500/10 border-blue-500/30';
      default:
        return 'text-gray-400 bg-gray-500/10 border-gray-500/30';
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'low':
        return 'from-green-500 to-emerald-600';
      case 'medium':
        return 'from-yellow-500 to-orange-600';
      case 'high':
        return 'from-red-500 to-rose-600';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <div className="min-h-screen pt-16 pb-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-600 mb-6">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-gradient">Strategy Security Analysis</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Use AI to analyze your DeFi strategy code and identify security vulnerabilities and potential risks
          </p>
        </motion.div>

        {/* Input Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-6 mb-8"
        >
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Paste your strategy code
          </label>
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Paste your Solidity contract code or strategy code..."
            className="input-field min-h-[300px] resize-none font-mono text-sm"
            rows="15"
          />
          
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={() => setCode(exampleCode)}
              className="text-sm text-purple-400 hover:text-purple-300 flex items-center space-x-1"
            >
              <FileCode className="w-4 h-4" />
              <span>Use Example Code</span>
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleAnalyze}
              disabled={isAnalyzing || !code.trim()}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  <span>Start Analysis</span>
                </>
              )}
            </motion.button>
          </div>
        </motion.div>

        {/* Analysis Results */}
        {analysis && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Overall Score */}
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-white">Security Score</h2>
                <div className={`px-4 py-2 rounded-lg bg-gradient-to-br ${getRiskLevelColor(analysis.riskLevel)} text-white font-semibold`}>
                  {analysis.riskLevel.toUpperCase()}
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Overall Security Score</span>
                    <span className="text-2xl font-bold text-white">{analysis.overallScore}/100</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-3">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${analysis.overallScore}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className={`h-3 rounded-full bg-gradient-to-r ${
                        analysis.overallScore >= 80 ? 'from-green-500 to-emerald-600' :
                        analysis.overallScore >= 60 ? 'from-yellow-500 to-orange-600' :
                        'from-red-500 to-rose-600'
                      }`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Vulnerabilities */}
            {analysis.vulnerabilities.length > 0 && (
              <div className="card p-6">
                <div className="flex items-center space-x-2 mb-6">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                  <h2 className="text-2xl font-bold text-white">
                    Security Issues Found ({analysis.vulnerabilities.length})
                  </h2>
                </div>
                <div className="space-y-4">
                  {analysis.vulnerabilities.map((vuln, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`p-4 rounded-lg border ${getSeverityColor(vuln.severity)}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold">{vuln.type}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(vuln.severity)}`}>
                            {vuln.severity.toUpperCase()}
                          </span>
                        </div>
                        {vuln.line && (
                          <span className="text-sm text-gray-400">Line {vuln.line}</span>
                        )}
                      </div>
                      <p className="text-gray-300 mb-2">{vuln.description}</p>
                      <div className="mt-2 p-2 bg-black/20 rounded">
                        <p className="text-sm text-gray-400">
                          <span className="text-green-400">Recommendation:</span> {vuln.recommendation}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            <div className="card p-6">
              <div className="flex items-center space-x-2 mb-6">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <h2 className="text-2xl font-bold text-white">Improvement Recommendations</h2>
              </div>
              <ul className="space-y-3">
                {analysis.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Gas Optimization */}
            {analysis.gasOptimization && analysis.gasOptimization.length > 0 && (
              <div className="card p-6">
                <h2 className="text-2xl font-bold text-white mb-4">Gas Optimization Suggestions</h2>
                <ul className="space-y-2">
                  {analysis.gasOptimization.map((opt, index) => (
                    <li key={index} className="flex items-start space-x-3 text-gray-300">
                      <span className="text-blue-400">•</span>
                      <span>{opt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        )}

        {/* Info Section */}
        {!analysis && !isAnalyzing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="card p-6 mt-8"
          >
            <h3 className="text-lg font-semibold text-white mb-3">🔒 Security Analysis Features</h3>
            <ul className="space-y-2 text-gray-400">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Automatically detect common security vulnerabilities (reentrancy attacks, integer overflow, access control, etc.)</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Analyze code quality and best practices</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Provide detailed fix recommendations and code optimization solutions</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Gas usage optimization suggestions to reduce transaction costs</span>
              </li>
            </ul>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default SecurityAnalyzer;


